import { MOBILE_AI_NOTES_BADGE_SIZE, MOBILE_AI_NOTES_FAB_SIZE, MOBILE_CHAT_PILL_HEIGHT, MOBILE_CHAT_PILL_MAX_WIDTH, MOBILE_CHAT_PILL_UNREAD_DOT_SIZE, MOBILE_TOOLBOX_SHORTCUTS_GAP, MOBILE_TOOLBOX_SHORTCUTS_INSET, QUICK_CHAT_INPUT_HEIGHT, QUICK_CHAT_MAX_WIDTH, QUICK_CHAT_MIN_WIDTH, QUICK_CHAT_PILL_BORDER_RADIUS, QUICK_CHAT_PREVIEW_BORDER_RADIUS, QUICK_CHAT_PREVIEW_FADE_OUT_MS, QUICK_CHAT_PREVIEW_GAP, QUICK_CHAT_PREVIEW_MAX_WIDTH, QUICK_CHAT_PREVIEW_SLIDE_DISTANCE_PX, QUICK_CHAT_PREVIEW_SLIDE_IN_MS, QUICK_CHAT_PREVIEW_Z_INDEX, TOOLBAR_BUTTON_BORDER_RADIUS, TOOLBAR_FULL_WIDTH_HEIGHT, TOOLBAR_HORIZONTAL_PADDING, TOOLBAR_ICON_SIZE, TOOLBAR_LABELED_BUTTON_CLASS, TOOLBAR_LABELED_HANGUP_CLASS, TOOLBAR_LABEL_FONT_SIZE, TOOLBAR_SETTINGS_CARET_RIGHT, TOOLBAR_SETTINGS_CARET_SIZE, TOOLBAR_SETTINGS_CARET_TOP } from '../../meeting-ui/constants';
export * from './constants.any';
/**
 * Returns an object containing the declaration of the common, reusable CSS classes.
 *
 * @param {Object} theme -The theme.
 *
 * @returns {Object} - The common styles.
 */
export const commonStyles = (theme) => {
    return {
        ':root': {
            // Inject semantic tokens as CSS custom properties for use in SCSS
            '--body-background-color': theme.palette.uiBackground,
            '--body-text-color': theme.palette.text01,
            '--drawer-background-color': theme.palette.drawerBackground,
            '--icon-svg-fill': theme.palette.iconSvgFill,
            '--large-video-background-color': theme.palette.largeVideoBackground,
            '--overflow-menu-background-color': theme.palette.overflowMenuBackground,
            '--overflow-menu-item-disabled-color': theme.palette.overflowMenuItemDisabled,
            '--overflow-menu-item-hover-color': theme.palette.overflowMenuItemHover,
            '--overflow-menu-item-icon-color': theme.palette.overflowMenuItemIcon,
            '--overflow-menu-item-text-color': theme.palette.overflowMenuItemText,
            '--prejoin-preview-background': theme.palette.prejoinPreviewBackground,
            '--reactions-menu-background': theme.palette.reactionsMenuBackground,
            '--reactions-menu-box-shadow-1': theme.palette.reactionsMenuBoxShadow1,
            '--reactions-menu-box-shadow-2': theme.palette.reactionsMenuBoxShadow2,
            '--reactions-menu-button-toggled': theme.palette.reactionsMenuButtonToggled,
            '--toolbar-button-active-color': theme.palette.toolbarButtonActive,
            '--toolbar-button-color': theme.palette.toolbarButton,
            '--toolbar-button-hover-color': theme.palette.toolbarButtonHover,
            '--toolbar-icon-active-color': theme.palette.toolbarIconActive,
            '--toolbar-icon-color': theme.palette.toolbarIcon,
            '--toolbar-icon-hover-color': theme.palette.toolbarIconHover,
            '--toolbox-background-color': theme.palette.toolboxBackground,
            '--toolbar-border-color': theme.palette.toolbarBorderColor,
            '--speaking-indicator-background': theme.palette.speakingIndicatorBackground,
            '--speaking-indicator-text-color': theme.palette.speakingIndicatorText,
            '--conference-header-background': theme.palette.conferenceHeaderBackground,
            '--hangup-button-background': theme.palette.hangupButtonBackground,
            '--hangup-button-hover-background': theme.palette.hangupButtonHover,
            '--hangup-button-active-background': theme.palette.hangupButtonActive,
            '--active-speaker-border-color': theme.palette.activeSpeakerBorder,
            '--thumbnail-label-background': theme.palette.thumbnailLabelBackground,
            '--toolbar-active-screen-share': theme.palette.toolbarActiveScreenShare,
            '--toolbar-settings-caret-size': `${TOOLBAR_SETTINGS_CARET_SIZE}px`,
            '--toolbar-settings-caret-top': `${TOOLBAR_SETTINGS_CARET_TOP}px`,
            '--toolbar-settings-caret-right': `${TOOLBAR_SETTINGS_CARET_RIGHT}px`,
            '--circle-avatar-border': theme.palette.circleAvatarBorder,
            '--circle-avatar-ring': theme.palette.circleAvatarRing,
            '--apps-grid-icon-select-background': theme.palette.appsGridIconSelectBackground,
            '--apps-grid-icon-closedcaptions': theme.palette.appsGridIconClosedCaptions,
            '--apps-grid-icon-audiotranslation': theme.palette.appsGridIconAudioTranslation,
            '--apps-grid-icon-whiteboard': theme.palette.appsGridIconWhiteboard,
            '--apps-grid-icon-hover-filter': theme.palette.uiBackground === '#F7F8FA'
                ? 'brightness(0.96)'
                : 'brightness(1.18)'
        },
        '.empty-list': {
            listStyleType: 'none',
            margin: 0,
            padding: 0
        },
        '.mute-dialog': {
            '& .separator-line': {
                margin: `${theme.spacing(4)} 0 ${theme.spacing(4)} -20px`,
                padding: '0 20px',
                width: '100%',
                height: '1px',
                background: '#5E6D7A'
            },
            '& .control-row': {
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: theme.spacing(3),
                '& label': {
                    fontSize: '0.875rem'
                }
            }
        },
        '.overflow-menu-item': {
            alignItems: 'center',
            color: theme.palette.overflowMenuItemText,
            cursor: 'pointer',
            display: 'flex',
            fontSize: '0.875rem',
            fontWeight: 400,
            height: 40,
            lineHeight: '1.5rem',
            padding: '8px 16px',
            boxSizing: 'border-box',
            '& > div': {
                display: 'flex',
                alignItems: 'center'
            },
            '&.unclickable': {
                cursor: 'default'
            },
            '&.disabled': {
                cursor: 'initial',
                color: theme.palette.overflowMenuItemDisabled,
                '&:hover': {
                    background: 'none'
                },
                '& svg': {
                    fill: theme.palette.overflowMenuItemDisabled
                }
            },
            '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                    background: theme.palette.overflowMenuItemHover
                },
                '&.unclickable:hover': {
                    background: 'inherit'
                }
            }
        },
        '.overflow-menu-item-icon': {
            marginRight: '16px',
            '& i': {
                display: 'inline',
                fontSize: '1.5rem'
            },
            '@media (hover: hover) and (pointer: fine)': {
                '&i:hover': {
                    backgroundColor: 'initial'
                }
            },
            '& img': {
                maxWidth: 24,
                maxHeight: 24
            },
            '& svg': {
                fill: theme.palette.overflowMenuItemIcon,
                height: 20,
                width: 20
            }
        },
        '.prejoin-dialog': {
            backgroundColor: theme.palette.prejoinDialogBackground,
            boxShadow: '0px 2px 20px rgba(0, 0, 0, 0.5)',
            borderRadius: theme.shape.borderRadius,
            color: '#fff',
            height: '400px',
            width: '375px',
            '.prejoin-dialog--small': {
                height: 300,
                width: 400
            },
            '.prejoin-dialog-label': {
                fontSize: '1rem',
                lineHeight: '1.5rem'
            },
            '.prejoin-dialog-label-num': {
                background: '#2b3b4b',
                border: '1px solid #A4B8D1',
                borderRadius: '50%',
                color: '#fff',
                display: 'inline-block',
                height: '24px',
                marginRight: theme.spacing(2),
                width: '24px'
            },
            '.prejoin-dialog-container': {
                alignItems: 'center',
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
                height: '100dvh',
                justifyContent: 'center',
                left: 0,
                position: 'absolute',
                top: 0,
                width: '100vw',
                zIndex: 3
            },
            '.prejoin-dialog-flag': {
                display: 'inline-block',
                marginRight: theme.spacing(2),
                transform: 'scale(1.2)'
            },
            '.prejoin-dialog-title': {
                display: 'inline-block',
                fontSize: '1.5rem',
                lineHeight: '2rem'
            },
            '.prejoin-dialog-icon': {
                cursor: 'pointer'
            },
            '.prejoin-dialog-btn': {
                marginBottom: '8px'
            },
            '.prejoin-dialog-dialin-container': {
                textAlign: 'center'
            },
            '.prejoin-dialog-delimiter': {
                background: theme.palette.prejoinDialogDelimiter,
                border: '0',
                height: '1px',
                margin: '0',
                padding: '0',
                width: '100%'
            },
            '.prejoin-dialog-delimiter-container': {
                margin: `${theme.spacing(4)} 0`,
                position: 'relative'
            },
            '.prejoin-dialog-delimiter-txt-container': {
                position: 'absolute',
                textAlign: 'center',
                top: '-8px',
                width: '100%'
            },
            '.prejoin-dialog-delimiter-txt': {
                background: theme.palette.prejoinDialogBackground,
                color: theme.palette.prejoinDialogDelimiterText,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                padding: `0 ${theme.spacing(2)}`
            }
        },
        '.prejoin-dialog-btn': {
            '&.primary, &.prejoin-dialog-btn.text': {
                width: '310px'
            }
        },
        '.toolbox-icon': {
            display: 'flex',
            borderRadius: TOOLBAR_BUTTON_BORDER_RADIUS,
            flexDirection: 'column',
            fontSize: '1.5rem',
            height: 48,
            justifyContent: 'center',
            width: 48,
            '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                    backgroundColor: theme.palette.toolboxIconHover
                },
                '&:active': {
                    backgroundColor: theme.palette.toolboxIconActive
                }
            },
            [theme.breakpoints.down(320)]: {
                height: 36,
                width: 36
            },
            '&.toggled': {
                backgroundColor: theme.palette.toolboxIconToggled
            },
            '&.screen-share-toggled': {
                backgroundColor: theme.palette.toolbarActiveScreenShare
            },
            '&.disabled': {
                cursor: 'initial !important',
                backgroundColor: `${theme.palette.disabled01} !important`,
                '& svg': {
                    fill: `${theme.palette.icon03} !important`
                }
            }
        },
        '.toolbar-button-with-badge': {
            display: 'inline-block',
            overflow: 'visible',
            position: 'relative',
            '& .badge-round': {
                bottom: 'auto',
                fontSize: '0.75rem',
                lineHeight: '1.25rem',
                minWidth: 20,
                pointerEvents: 'none',
                position: 'absolute',
                right: -5,
                top: -5,
                zIndex: 1
            },
            '&:has(> .toolbar-labeled-button) .badge-round': {
                right: 4,
                top: 0
            },
            '&:has(> .apps-overflow-grid-item) .badge-round': {
                right: 4,
                top: 4
            }
        },
        [`.${TOOLBAR_LABELED_BUTTON_CLASS}`]: {
            alignItems: 'center',
            cursor: 'pointer',
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 56,
            padding: '4px 8px',
            borderRadius: TOOLBAR_BUTTON_BORDER_RADIUS,
            '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                    backgroundColor: theme.palette.toolboxIconHover
                },
                '&:active': {
                    backgroundColor: theme.palette.toolboxIconActive
                }
            },
            '&.disabled': {
                cursor: 'initial',
                opacity: 0.5
            },
            [`& .${TOOLBAR_LABELED_BUTTON_CLASS}-icon`]: {
                alignItems: 'center',
                borderRadius: TOOLBAR_BUTTON_BORDER_RADIUS,
                display: 'flex',
                height: 40,
                justifyContent: 'center',
                width: 40,
                '& svg': {
                    fill: theme.palette.toolbarIcon,
                    height: TOOLBAR_ICON_SIZE,
                    width: TOOLBAR_ICON_SIZE
                },
                '&.toggled': {
                    backgroundColor: theme.palette.toolboxIconToggled
                },
                '&.screen-share-toggled': {
                    backgroundColor: theme.palette.toolbarActiveScreenShare
                }
            },
            [`& .${TOOLBAR_LABELED_BUTTON_CLASS}-label`]: {
                color: theme.palette.text02,
                fontSize: TOOLBAR_LABEL_FONT_SIZE,
                lineHeight: '1rem',
                marginTop: 2,
                maxWidth: 64,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            },
            [`& .${TOOLBAR_LABELED_BUTTON_CLASS}-label-hangup`]: {
                color: theme.palette.hangupButtonBackground
            },
            [`&.${TOOLBAR_LABELED_HANGUP_CLASS} .${TOOLBAR_LABELED_BUTTON_CLASS}-icon svg`]: {
                fill: theme.palette.hangupButtonBackground
            }
        },
        [`.toolbox-content-mobile .${TOOLBAR_LABELED_BUTTON_CLASS}`]: {
            flex: 1,
            minWidth: 0,
            padding: '2px 0',
            [`& .${TOOLBAR_LABELED_BUTTON_CLASS}-icon`]: {
                background: 'transparent',
                height: 28,
                width: 28,
                '&.toggled': {
                    background: 'transparent'
                },
                '&.screen-share-toggled': {
                    background: 'transparent'
                },
                '&.desktop svg': {
                    fill: theme.palette.success01
                }
            },
            [`& .${TOOLBAR_LABELED_BUTTON_CLASS}-label`]: {
                maxWidth: '100%'
            }
        },
        '.toolbox-content-mobile .toolbox-button-wth-dialog': {
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            minWidth: 0
        },
        '.toolbar-quick-input': {
            display: 'flex',
            flexDirection: 'column',
            maxWidth: QUICK_CHAT_MAX_WIDTH,
            minWidth: QUICK_CHAT_MIN_WIDTH,
            // 预览气泡 absolute 向上伸出，父级勿裁切
            overflow: 'visible',
            position: 'relative',
            zIndex: QUICK_CHAT_PREVIEW_Z_INDEX
        },
        '.toolbar-quick-input-previews': {
            bottom: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            left: 0,
            marginBottom: QUICK_CHAT_PREVIEW_GAP,
            maxWidth: QUICK_CHAT_PREVIEW_MAX_WIDTH,
            pointerEvents: 'all',
            position: 'absolute',
            right: 0,
            width: '100%',
            zIndex: QUICK_CHAT_PREVIEW_Z_INDEX
        },
        '@keyframes toolbarQuickChatPreviewIn': {
            from: {
                opacity: 0,
                transform: `translateY(${QUICK_CHAT_PREVIEW_SLIDE_DISTANCE_PX}px)`
            },
            to: {
                opacity: 1,
                transform: 'translateY(0)'
            }
        },
        '.toolbar-quick-input-preview': {
            // 重置 button UA 默认边框（黑边来源）
            appearance: 'none',
            animation: `toolbarQuickChatPreviewIn ${QUICK_CHAT_PREVIEW_SLIDE_IN_MS}ms ease-out`,
            backgroundColor: theme.palette.quickChatPreviewBackground,
            border: 'none',
            borderRadius: QUICK_CHAT_PREVIEW_BORDER_RADIUS,
            boxSizing: 'border-box',
            color: theme.palette.text01,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '0.75rem',
            lineHeight: 1.4,
            maxWidth: '100%',
            opacity: 1,
            overflow: 'hidden',
            padding: '6px 10px',
            textAlign: 'left',
            textOverflow: 'ellipsis',
            transform: 'translateY(0)',
            transition: [
                `opacity ${QUICK_CHAT_PREVIEW_FADE_OUT_MS}ms ease`,
                `transform ${QUICK_CHAT_PREVIEW_FADE_OUT_MS}ms ease`
            ].join(', '),
            whiteSpace: 'nowrap',
            '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                    backgroundColor: theme.palette.quickChatPreviewBackgroundHover
                }
            }
        },
        '.toolbar-quick-input-preview-fading': {
            animation: 'none',
            opacity: 0,
            pointerEvents: 'none',
            transform: `translateY(-${QUICK_CHAT_PREVIEW_SLIDE_DISTANCE_PX}px)`
        },
        '.toolbar-quick-input-preview-name': {
            color: theme.palette.text02,
            marginRight: 4
        },
        '.toolbar-quick-input-bar': {
            alignItems: 'center',
            backgroundColor: theme.palette.ui02,
            border: `1px solid ${theme.palette.toolbarBorderColor}`,
            borderRadius: QUICK_CHAT_PILL_BORDER_RADIUS,
            boxSizing: 'border-box',
            display: 'flex',
            gap: 6,
            height: QUICK_CHAT_INPUT_HEIGHT,
            padding: '0 8px 0 10px',
            width: '100%'
        },
        '.toolbar-quick-input-bar-disabled': {
            opacity: 0.6
        },
        '.toolbar-quick-input-icon-btn': {
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            borderRadius: '50%',
            color: theme.palette.icon02,
            cursor: 'pointer',
            display: 'inline-flex',
            flexShrink: 0,
            height: 28,
            justifyContent: 'center',
            padding: 0,
            width: 28,
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            },
            '& svg': {
                fill: 'currentColor',
                height: 18,
                width: 18
            }
        },
        '.toolbar-quick-input-field': {
            background: 'transparent',
            border: 0,
            color: theme.palette.text01,
            flex: 1,
            fontSize: '0.75rem',
            minWidth: 0,
            outline: 'none',
            padding: 0,
            '&::placeholder': {
                color: theme.palette.text02
            },
            '&:disabled': {
                cursor: 'not-allowed'
            }
        },
        '.toolbar-quick-input-smileys': {
            backgroundColor: theme.palette.ui01,
            border: `1px solid ${theme.palette.toolbarBorderColor}`,
            borderRadius: QUICK_CHAT_PREVIEW_BORDER_RADIUS,
            bottom: `calc(100% + ${QUICK_CHAT_PREVIEW_GAP}px)`,
            boxSizing: 'border-box',
            left: 0,
            maxWidth: QUICK_CHAT_MAX_WIDTH,
            overflow: 'auto',
            position: 'absolute',
            width: '100%',
            zIndex: QUICK_CHAT_PREVIEW_Z_INDEX,
            '& #smileysContainer': {
                display: 'flex',
                flexWrap: 'wrap',
                maxHeight: 160,
                overflow: 'auto',
                padding: 4
            }
        },
        '.toolbox-button': {
            color: theme.palette.toolbarIcon,
            cursor: 'pointer',
            display: 'inline-block',
            lineHeight: '3rem',
            textAlign: 'center'
        },
        '.toolbox-content-items': {
            alignItems: 'center',
            background: theme.palette.toolboxBackground,
            borderRadius: 0,
            borderTop: `1px solid ${theme.palette.toolbarBorderColor}`,
            boxShadow: 'none',
            boxSizing: 'border-box',
            display: 'grid',
            // 中间区可收缩，保证右侧挂断始终落在 100% 宽容器内、不被挤出视口
            // 横向约束靠 minmax(0,…) + 中间 overflowX；勿用 overflow:hidden，
            // 否则会裁切左侧快捷聊天向上悬浮的预览气泡（BUG-2026-0806-012）
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, max-content) minmax(0, 1fr)',
            margin: 0,
            maxWidth: '100%',
            minHeight: TOOLBAR_FULL_WIDTH_HEIGHT,
            overflow: 'visible',
            padding: `8px ${TOOLBAR_HORIZONTAL_PADDING}px`,
            pointerEvents: 'all',
            textAlign: 'center',
            width: '100%',
            '& > div': {
                marginRight: 0
            }
        },
        // 窄屏：单列均分主钮（麦克风/摄像头/共享/参会者/更多）
        '.toolbox-content-mobile .toolbox-content-items': {
            gridTemplateColumns: '1fr',
            padding: '8px 4px'
        },
        '.toolbox-section-left': {
            alignItems: 'center',
            display: 'flex',
            flexShrink: 1,
            gap: 8,
            justifyContent: 'flex-start',
            minWidth: 0,
            // clip 横向溢出，避免快捷输入+举手挤进中间 mic/cam；
            // 用 clip 而非 hidden，以免把 overflow-y 算成 auto 裁切向上预览气泡（BUG-2026-0806-012）
            overflowX: 'clip',
            overflowY: 'visible',
            paddingRight: 8,
            position: 'relative',
            zIndex: QUICK_CHAT_PREVIEW_Z_INDEX
        },
        '.toolbox-section-center': {
            alignItems: 'center',
            display: 'flex',
            gap: 4,
            justifyContent: 'center',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'auto',
            padding: '0 8px'
        },
        '.toolbox-section-narrow-main': {
            justifyContent: 'space-around',
            overflowX: 'hidden',
            padding: '0 4px',
            width: '100%'
        },
        '.toolbox-section-right': {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'flex-end',
            minWidth: 'min-content',
            paddingLeft: 8,
            paddingRight: 0
        },
        '.toolbox-content-wrapper': {
            overflow: 'visible',
            position: 'relative'
        },
        '.mobile-toolbox-shortcuts': {
            alignItems: 'flex-end',
            bottom: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            left: 0,
            marginBottom: MOBILE_TOOLBOX_SHORTCUTS_GAP,
            padding: `0 ${MOBILE_TOOLBOX_SHORTCUTS_INSET}px`,
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
            zIndex: QUICK_CHAT_PREVIEW_Z_INDEX
        },
        '.mobile-toolbox-chat': {
            flex: 1,
            maxWidth: `min(${MOBILE_CHAT_PILL_MAX_WIDTH}px, calc(100% - ${MOBILE_AI_NOTES_FAB_SIZE + MOBILE_TOOLBOX_SHORTCUTS_INSET}px))`,
            minWidth: 0,
            overflow: 'visible',
            pointerEvents: 'all',
            position: 'relative',
            zIndex: QUICK_CHAT_PREVIEW_Z_INDEX
        },
        '.mobile-toolbox-chat-only': {
            maxWidth: '100%'
        },
        '.mobile-toolbox-chat-fab': {
            alignItems: 'center',
            appearance: 'none',
            backgroundColor: theme.palette.quickChatPreviewBackground,
            border: 'none',
            borderRadius: '50%',
            color: theme.palette.icon01,
            cursor: 'pointer',
            display: 'inline-flex',
            flexShrink: 0,
            height: MOBILE_AI_NOTES_FAB_SIZE,
            justifyContent: 'center',
            padding: 0,
            pointerEvents: 'all',
            position: 'relative',
            touchAction: 'manipulation',
            width: MOBILE_AI_NOTES_FAB_SIZE
        },
        '.mobile-toolbox-chat-fab-icon': {
            display: 'inline-flex',
            position: 'relative',
            '& svg': {
                fill: 'currentColor',
                height: 22,
                width: 22
            }
        },
        '.mobile-toolbox-chat-smileys': {
            maxWidth: `min(${QUICK_CHAT_MAX_WIDTH}px, calc(100vw - 24px))`,
            width: `min(${QUICK_CHAT_MAX_WIDTH}px, calc(100vw - 24px))`
        },
        '.mobile-toolbox-chat-pill': {
            alignItems: 'center',
            backgroundColor: theme.palette.quickChatPreviewBackground,
            borderRadius: QUICK_CHAT_PILL_BORDER_RADIUS,
            boxSizing: 'border-box',
            color: theme.palette.icon01,
            display: 'flex',
            fontFamily: 'inherit',
            gap: 6,
            height: MOBILE_CHAT_PILL_HEIGHT,
            maxWidth: '100%',
            minWidth: 0,
            padding: '0 8px 0 6px',
            width: '100%',
            '& svg': {
                fill: 'currentColor',
                flexShrink: 0,
                height: 18,
                width: 18
            }
        },
        '.mobile-toolbox-chat-pill-disabled': {
            opacity: 0.6
        },
        '.mobile-toolbox-chat-pill-btn': {
            alignItems: 'center',
            appearance: 'none',
            background: 'transparent',
            border: 0,
            borderRadius: '50%',
            color: 'inherit',
            cursor: 'pointer',
            display: 'inline-flex',
            flexShrink: 0,
            height: 28,
            justifyContent: 'center',
            padding: 0,
            touchAction: 'manipulation',
            width: 28,
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            }
        },
        '.mobile-toolbox-chat-pill-icon': {
            display: 'inline-flex',
            flexShrink: 0,
            position: 'relative'
        },
        '.mobile-toolbox-chat-pill-dot': {
            backgroundColor: theme.palette.success01,
            border: `1px solid ${theme.palette.quickChatPreviewBackground}`,
            borderRadius: '50%',
            height: MOBILE_CHAT_PILL_UNREAD_DOT_SIZE,
            pointerEvents: 'none',
            position: 'absolute',
            right: -2,
            top: -1,
            width: MOBILE_CHAT_PILL_UNREAD_DOT_SIZE
        },
        '.mobile-toolbox-chat-pill-divider': {
            alignSelf: 'stretch',
            backgroundColor: theme.palette.icon02,
            flexShrink: 0,
            margin: '8px 0',
            opacity: 0.45,
            width: 1
        },
        '.mobile-toolbox-chat-pill-field': {
            background: 'transparent',
            border: 0,
            color: theme.palette.text01,
            flex: 1,
            fontFamily: 'inherit',
            fontSize: '0.8125rem',
            minWidth: 0,
            outline: 'none',
            padding: 0,
            '&::placeholder': {
                color: theme.palette.text02
            },
            '&:disabled': {
                cursor: 'not-allowed'
            }
        },
        '.mobile-toolbox-ai-fab': {
            alignItems: 'center',
            appearance: 'none',
            backgroundColor: theme.palette.quickChatPreviewBackground,
            border: 'none',
            borderRadius: '50%',
            color: theme.palette.icon01,
            cursor: 'pointer',
            display: 'inline-flex',
            flexShrink: 0,
            height: MOBILE_AI_NOTES_FAB_SIZE,
            justifyContent: 'center',
            marginLeft: 'auto',
            padding: 0,
            pointerEvents: 'all',
            position: 'relative',
            touchAction: 'manipulation',
            width: MOBILE_AI_NOTES_FAB_SIZE,
            '& .mobile-toolbox-ai-fab-doc svg': {
                fill: 'currentColor',
                height: 20,
                width: 20
            }
        },
        '.mobile-toolbox-ai-fab-doc': {
            display: 'inline-flex'
        },
        '.mobile-toolbox-ai-fab-badge': {
            alignItems: 'center',
            backgroundColor: theme.palette.success01,
            borderRadius: '50%',
            bottom: 2,
            color: theme.palette.text04,
            display: 'inline-flex',
            height: MOBILE_AI_NOTES_BADGE_SIZE,
            justifyContent: 'center',
            pointerEvents: 'none',
            position: 'absolute',
            right: 2,
            width: MOBILE_AI_NOTES_BADGE_SIZE,
            '& svg': {
                fill: 'none',
                height: 8,
                stroke: 'currentColor',
                width: 8
            }
        },
        '.apps-overflow-panel': {
            background: theme.palette.overflowMenuBackground,
            borderRadius: 12,
            boxSizing: 'border-box',
            display: 'flex',
            flex: '1 1 auto',
            height: 265,
            maxHeight: 265,
            maxWidth: 460,
            minHeight: 265,
            minWidth: 460,
            overflow: 'hidden',
            width: 460
        },
        '.apps-overflow-panel.apps-overflow-panel-drawer': {
            background: 'transparent',
            borderRadius: 0,
            flex: '1 1 auto',
            flexDirection: 'column',
            height: 'auto',
            maxHeight: 'none',
            maxWidth: 'none',
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto',
            width: '100%'
        },
        '.apps-overflow-grid': {
            alignContent: 'start',
            alignItems: 'start',
            display: 'grid',
            flex: '1 1 auto',
            gap: 8,
            gridAutoRows: 'max-content',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            maxHeight: '100%',
            minWidth: 0,
            overflowY: 'auto',
            padding: 12
        },
        '.apps-overflow-panel-drawer .apps-overflow-grid': {
            flex: '0 0 auto',
            gap: '16px 0',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            justifyItems: 'center',
            overflowY: 'visible',
            padding: '12px 8px 16px'
        },
        '.apps-overflow-extended-title': {
            alignItems: 'center',
            color: theme.palette.text03,
            display: 'flex',
            fontSize: '0.75rem',
            gap: 8,
            lineHeight: 1.2,
            padding: '12px 16px 8px',
            '&::before, &::after': {
                backgroundColor: theme.palette.toolbarBorderColor,
                content: '""',
                flex: 1,
                height: 1
            }
        },
        '.apps-overflow-list': {
            display: 'flex',
            flex: '0 0 168px',
            flexDirection: 'column',
            minHeight: 0,
            overflowY: 'auto',
            padding: '8px 8px 8px 4px',
            '& > div': {
                padding: '0 !important'
            },
            '& [role="button"], & [role="menuitem"]': {
                minHeight: 28,
                padding: '4px 8px',
                '& > *:not(:last-child)': {
                    marginRight: 8
                },
                '& svg': {
                    height: 16,
                    width: 16
                }
            }
        },
        '.apps-overflow-panel-drawer .apps-overflow-list': {
            borderLeft: 'none',
            borderTop: `1px solid ${theme.palette.toolbarBorderColor}`,
            flex: '1 1 auto'
        },
        '.apps-overflow-grid-item': {
            alignItems: 'center',
            alignSelf: 'start',
            background: theme.palette.toolboxIconHover,
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            height: 'auto',
            justifyContent: 'center',
            minWidth: 0,
            padding: '8px 4px 6px',
            '&:hover .apps-overflow-grid-icon': {
                filter: 'var(--apps-grid-icon-hover-filter)'
            },
            '&.disabled': {
                cursor: 'default',
                opacity: 0.45
            }
        },
        '.apps-overflow-grid-icon': {
            alignItems: 'center',
            borderRadius: 8,
            color: theme.palette.icon01,
            display: 'flex',
            flexShrink: 0,
            height: 32,
            justifyContent: 'center',
            width: 32,
            '& svg': {
                fill: 'currentColor',
                height: 20,
                width: 20
            }
        },
        '.apps-overflow-grid-label': {
            color: theme.palette.text01,
            fontSize: '0.6875rem',
            lineHeight: 1.25,
            maxWidth: '100%',
            overflow: 'hidden',
            textAlign: 'center',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        '.apps-overflow-panel-drawer .apps-overflow-grid-icon': {
            background: 'transparent',
            flexShrink: 0,
            height: 28,
            margin: '0 auto',
            width: 28,
            '& svg': {
                fill: 'currentColor',
                height: 24,
                width: 24
            }
        },
        '.apps-overflow-panel-drawer .apps-overflow-grid-item': {
            alignItems: 'center',
            background: 'transparent',
            borderRadius: 0,
            boxSizing: 'border-box',
            gap: 6,
            height: '100%',
            justifyContent: 'flex-start',
            minWidth: 0,
            padding: '0 4px',
            width: '100%',
            '&:hover .apps-overflow-grid-icon': {
                filter: 'none'
            }
        },
        '.apps-overflow-panel-drawer .apps-overflow-grid-label': {
            display: '-webkit-box',
            fontSize: '0.6875rem',
            lineHeight: 1.25,
            maxWidth: '100%',
            minHeight: '2.5em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            whiteSpace: 'normal',
            width: '100%',
            wordBreak: 'break-all'
        },
    };
};
